<template>
  <base-container fluid class="pa-0">
    <base-row
      v-if="totalRecipesOnLoad > pageNumber * pageSize"
      justify="center"
      no-gutters
    >
      <base-col cols="6"
        ><base-row justify="start"
          ><base-col cols="auto"
            ><p class="text-medium-emphasis">
              {{ t('message.suggestedAudienceSegment') }}
            </p></base-col
          ></base-row
        ></base-col
      >
      <base-col
        cols="6"
        class="cursor-pointer text-primary text-right pa-0 pr-3"
        @click="handleViewAllClick"
        ><base-row justify="end"
          ><base-col cols="auto"
            ><p>
              {{ t('message.viewMore') }}
              <base-icon>mdi-unfold-more-horizontal</base-icon>
            </p></base-col
          ></base-row
        ></base-col
      >
    </base-row>
    <base-row>
      <base-col
        v-for="(recipe, index) in firstFewRecipes"
        :key="index"
        xs="12"
        sm="6"
        md="4"
        lg="3"
      >
        <customer-recipe
          :id="recipe.id"
          :name="recipe.name"
          :description="recipe.description"
        />
      </base-col>
    </base-row>
    <custom-modal
      v-model="isViewAllClicked"
      max-width="85%"
      height="80%"
      :add-card-actions="false"
      :add-card-dismiss="true"
      @close="handleClose"
    >
      <template #title> {{ t('messages.audienceRecipes') }} </template>
      <template #content>
        <base-row class="mb-1 px-2">
          <base-col sm="6" md="3">
            <base-text-field
              v-model="searchedText"
              density="compact"
              :label="$t('labels.searchAudiencRecipe')"
              prepend-inner-icon="mdi-magnify"
              variant="outlined"
              flat
              hide-details
            ></base-text-field>
          </base-col>
        </base-row>
        <base-row v-if="recipes.length == 0" justify="center" align="center">
          <base-col
            ><p class="text-h6 text-center">No recipes available</p></base-col
          ></base-row
        >
        <PerfectScrollbar v-else>
          <div
            ref="scrollalbeContainer"
            class="scroll-container"
            @scroll="handleScroll"
          >
            <base-row style="height: 100%">
              <base-col
                v-for="(recipe, index) in recipes"
                :key="index"
                xs="12"
                sm="6"
                md="4"
                lg="3"
              >
                <CustomerRecipe
                  :id="recipe.id"
                  :name="recipe.name"
                  :description="recipe.description"
                />
              </base-col>
            </base-row></div
        ></PerfectScrollbar>
      </template> </custom-modal
  ></base-container>
</template>

<script lang="ts" setup>
  import { onMounted, ref, watch, computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useAppStore } from '@/store/app'
  import {
    BaseRow,
    BaseCol,
    BaseContainer,
    BaseIcon,
    BaseTextField
  } from '@deltax/vuetify-wrapper-components'
  import CustomModal from '@/components/customComponents/CustomModal.vue'
  import CustomerRecipe from './CustomerRecipe.vue'
  import SegmentTemplateService from '@/services/segmentTemplateService'
  import { SegmentTemplateResponse } from '@/types/segmentTemplateResponse'
  import { PaginatedResponse } from '@/types/PaginatedResponse'
  import { useDisplay } from 'vuetify'
  import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
  import { debounce } from 'lodash'
  const { xs, sm, md } = useDisplay()

  interface Props {
    shouldOpenRecipesDialog?: boolean
  }
  const emit = defineEmits(['set-should-open-recipes-dialog'])
  const props = defineProps<Props>()
  watch(
    () => props.shouldOpenRecipesDialog,
    () => {
      isViewAllClicked.value = props.shouldOpenRecipesDialog
    }
  )

  const { t } = useI18n()
  const appStore = useAppStore()
  const segmentTemplateService = new SegmentTemplateService()
  const totalRecipes = ref(0)
  const totalRecipesOnLoad = ref(0)
  const pageNumber = ref(1)
  const pageSize = ref(12)
  const recipes = ref<SegmentTemplateResponse[]>([])
  const firstFewRecipes = ref<SegmentTemplateResponse[]>([])

  onMounted(async () => {
    isViewAllClicked.value = props.shouldOpenRecipesDialog
    let recipeResponse = await getCustomerSegmentTemplates()
    if (recipeResponse) {
      totalRecipes.value = recipeResponse.totalRecords
      totalRecipesOnLoad.value = recipeResponse.totalRecords
      recipes.value = recipeResponse.data
      let numberOfRecipesToShow = 4
      if (xs.value) {
        numberOfRecipesToShow = 1
      } else if (sm.value) {
        numberOfRecipesToShow = 2
      } else if (md.value) {
        numberOfRecipesToShow = 3
      }
      firstFewRecipes.value = recipes.value.slice(0, numberOfRecipesToShow)
    }
  })

  const getCustomerSegmentTemplates =
    async (): Promise<PaginatedResponse<SegmentTemplateResponse> | null> => {
      if (appStore.getSelectedBusinessProfile) {
        let recipeResponse = await segmentTemplateService.GetSegmentTemplates(
          appStore.getSelectedBusinessProfile.id,
          pageNumber.value,
          pageSize.value,
          searchedText.value
        )
        return recipeResponse
      }
      return null
    }

  const isViewAllClicked = ref(false)
  watch(isViewAllClicked, async val => {
    if (!val) {
      pageNumber.value = 1
      emit('set-should-open-recipes-dialog', isViewAllClicked.value)
    } else {
      pageNumber.value = 2
      await fetchRecipes()
    }
  })

  async function fetchRecipes() {
    if (pageNumber.value == 1) {
      let recipeResponse = await getCustomerSegmentTemplates()
      if (recipeResponse) {
        totalRecipes.value = recipeResponse.totalRecords
        recipes.value = recipeResponse.data
      }
    } else {
      // append
      let recipeResponse = await getCustomerSegmentTemplates()
      if (recipeResponse) {
        totalRecipes.value = recipeResponse.totalRecords
        recipes.value = [...recipes.value, ...recipeResponse.data]
      }
    }
  }
  const handleViewAllClick = () => {
    isViewAllClicked.value = true
  }
  const handleClose = () => {
    isViewAllClicked.value = false
  }

  const scrollalbeContainer = ref(null)
  const handleScroll = async () => {
    if (
      scrollalbeContainer.value &&
      isViewAllClicked.value &&
      scrollalbeContainer.value.scrollHeight -
        scrollalbeContainer.value.scrollTop <=
        scrollalbeContainer.value.clientHeight + 50
    ) {
      if (totalRecipes.value > pageNumber.value * pageSize.value) {
        pageNumber.value += 1
        await fetchRecipes()
      }
    }
  }

  const searchedText = ref('')
  watch(
    searchedText,
    debounce(async () => {
      pageNumber.value = 1
      recipes.value = []
      await fetchRecipes()
    }, 300)
  )
</script>

<style>
  .scroll-container {
  height: 63vh;
  overflow-y: auto;
  scrollbar-width: none; /* For Firefox */
}

.scroll-container::-webkit-scrollbar {
  display: none; /* For Chrome, Safari, Edge */
}
</style>
